import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, ExternalLink, X } from "lucide-react";
import api from "@/lib/api";

/**
 * Admin-only banner that flags email-delivery issues (e.g. Resend daily quota
 * exhausted). Polls /api/admin/email-health every 60s. Stays out of the way
 * — never blocks app usage. Quietly hidden when:
 *   - user is not admin
 *   - user is a demo account
 *   - email is unconfigured (deliberate off state, not a failure)
 *   - health endpoint reports healthy
 */
export default function EmailHealthBanner({ user }) {
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const fetchHealth = useCallback(async () => {
    if (!user || user.role !== "admin" || user.is_demo) return;
    try {
      const res = await api.get("/admin/email-health");
      setHealth(res.data);
    } catch (e) {
      // Silent — never block app usage. The banner just won't appear.
      setHealth(null);
    }
  }, [user]);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 60_000);
    return () => clearInterval(id);
  }, [fetchHealth]);

  if (!user || user.role !== "admin" || user.is_demo) return null;
  if (!health || health.healthy) return null;
  if (dismissed) return null;

  const reason = health.reason || "Recent email delivery failure";
  const failed24h = health?.last24h?.failed || 0;
  const isQuota = health.lastFailureCategory === "quota";

  return (
    <div
      data-testid="email-health-banner"
      className="border-b border-amber-300 bg-amber-50 text-amber-900"
    >
      <div className="mx-auto flex max-w-7xl items-start gap-3 px-4 py-3 md:px-8">
        <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" aria-hidden />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-medium" data-testid="email-health-banner-title">
            Email delivery issue detected
          </p>
          <p className="mt-0.5 text-amber-800/90" data-testid="email-health-banner-reason">
            {reason}
            {failed24h > 0 && (
              <span className="ml-1 text-amber-800/70">
                · {failed24h} failed in the last 24h
              </span>
            )}
          </p>
          {isQuota && (
            <p className="mt-1 text-xs text-amber-700">
              Resend's free tier has a daily send limit. Reminders and stage emails
              will resume once the limit resets, or after upgrading your Resend plan.
            </p>
          )}
          <a
            href="https://resend.com/emails"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="email-health-banner-link"
            className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-amber-900 underline-offset-2 hover:underline"
          >
            Open Resend dashboard <ExternalLink size={12} />
          </a>
        </div>
        <button
          type="button"
          data-testid="email-health-banner-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss email health banner"
          className="rounded-md p-1 text-amber-700 hover:bg-amber-100"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
